import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Dropdown } from 'primereact/dropdown'
import { Dialog } from 'primereact/dialog'
import Button from '../../components/ui/Button'
import Badge, { type BadgeTone } from '../../components/ui/Badge'
import PageHeader from '../../components/ui/PageHeader'
import TextInput from '../../components/ui/TextInput'
import MultiSelect from '../../components/ui/MultiSelect'
import FileUpload from '../../components/ui/FileUpload'
import FormField from '../../components/ui/FormField'
import FormTextField from '../../components/ui/form/FormTextField'
import FormNumberField from '../../components/ui/form/FormNumberField'
import FormDropdownField from '../../components/ui/form/FormDropdownField'
import FormDateField from '../../components/ui/form/FormDateField'
import { useToast } from '../../components/ui/ToastProvider'
import { useZodForm } from '../../lib/forms/useZodForm'
import {
  activityFormSchema,
  OTHER_EVENT_TYPE_ID,
  type ActivityFormValues,
} from './components/activityFormSchema'
import { useActivityLookups } from './hooks/useActivityLookups'
import { useCreateActivity } from './hooks/useCreateActivity'
import { getCities } from './api/activityApi'
import { formatDate, intlLocale } from '../../lib/datetime'
import { ATTACHMENT_ACCEPT } from '../../lib/uploadAccept'
import ScoreGauge, { GAUGE_COLOR } from './components/ScoreGauge'
import type { ActivityCreate } from './types'
import './activity.css'

const OTHER_STAKEHOLDER_ID = -1
const SCORE_OPTIONS = [0, 1, 2, 3, 4, 5].map((n) => ({ label: String(n), value: n }))
/** Öncelik bandı → rozet tonu (liste kalıbıyla aynı). */
const BAND_TONE: Record<string, BadgeTone> = {
  low: 'neutral',
  mid: 'blue',
  high: 'green',
}

interface UnitRow {
  uid: number
  unitName: string
  participationStatusId: number | null
  reason: string
}

/** Numaralı/uppercase bölüm başlığı (mockup: "1 · ETKİNLİK ÖZETİ — …"). Kartın üstünde durur. */
function SectionHead({ no, name, note }: Readonly<{ no: number; name: string; note?: string }>) {
  return (
    <div className="ev-sec-head">
      <span className="ev-sec-no">{no}</span>
      <span className="ev-sec-name">{name}</span>
      {note && <span className="ev-sec-note">— {note}</span>}
    </div>
  )
}

/**
 * Yeni Etkinlik (US2). 1. bölüm RHF+zod (Form*Field); alt bölümler (skor/paydaş/birim/dosya) form dışı
 * state. "Talep Oluştur" → doğrulama → "Talep Özeti — Son Kontrol" (salt-görünüm) → "Gönder" → create +
 * dosya yükleme → listeye dön. Katılım durumu sistemce Bekliyor (backend). (FR-030..040)
 */
export default function ActivityFormPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const lookups = useActivityLookups()
  const createMut = useCreateActivity()

  const { control, handleSubmit, watch, setValue } = useZodForm(activityFormSchema, {
    defaultValues: {
      nameTr: '',
      nameEn: '',
      organizingInstitution: '',
      startDate: '',
      endDate: '',
      numberOfParticipants: null,
      countryId: null,
      cityId: null,
      eventTypeId: null,
      otherEventType: '',
      invitationTypeId: null,
      featuredParticipants: '',
      website: '',
    },
  })

  const [scores, setScores] = useState<Record<number, number | undefined>>({})
  const [stakeholderIds, setStakeholderIds] = useState<number[]>([])
  const [stakeholderOtherTr, setStakeholderOtherTr] = useState('')
  const [units, setUnits] = useState<UnitRow[]>([])
  const [nextUid, setNextUid] = useState(0)
  const [files, setFiles] = useState<File[]>([])
  const [subError, setSubError] = useState<string | null>(null)
  const [pending, setPending] = useState<ActivityCreate | null>(null)

  const countryId = watch('countryId')
  const eventTypeId = watch('eventTypeId')

  const citiesQuery = useQuery({
    queryKey: ['activity-cities', countryId],
    queryFn: () => getCities(countryId as number),
    enabled: countryId != null,
  })

  // Ülke değişince şehir seçimi sıfırlanır (cascade).
  useEffect(() => {
    setValue('cityId', null)
  }, [countryId, setValue])

  if (lookups.isLoading || !lookups.data) {
    return (
      <div className="ui-page">
        <PageHeader title={t('activity.form.title')} />
        <div className="ui-loading">
          <i className="pi pi-spin pi-spinner" aria-hidden />
        </div>
      </div>
    )
  }
  const lk = lookups.data
  // Birim Görüşleri üst sınırı sistem parametresinden gelir (SYSTEM_PARAMETER: ACTIVITY_MAX_UNIT_OPINIONS);
  // eski/eksik yanıta karşı 10 varsayılanı.
  const maxUnitOpinions = lk.maxUnitOpinions ?? 10
  const opts = (items: { id: number; name: string }[]) =>
    items.map((i) => ({ label: i.name, value: i.id }))
  // Ülke/şehir gibi alfabetik listeler seçili dile göre sıralanır (Türkçe'de Ç/Ş/İ vb. harfler Z'den sonra
  // sıralanmasın diye locale-duyarlı karşılaştırma; DB sıralaması TR harfleri sona atıyordu).
  const collator = new Intl.Collator(i18n.language, { sensitivity: 'base' })
  const optsSorted = (items: { id: number; name: string }[]) =>
    opts(items).sort((a, b) => collator.compare(a.label, b.label))
  // Ülke dropdown'ında ev sahibi ülke (Türkiye) başa sabitlenir; kalanı locale-alfabetik.
  const HOME_COUNTRY_NAMES = ['türkiye', 'turkey']
  const countryOptions = () => {
    const sorted = optsSorted(lk.countries)
    const isHome = (o: { label: string }) =>
      HOME_COUNTRY_NAMES.includes(o.label.toLocaleLowerCase('tr-TR'))
    return [...sorted.filter(isHome), ...sorted.filter((o) => !isHome(o))]
  }
  const nameById = (items: { id: number; name: string }[], id: number | null | undefined) =>
    items.find((i) => i.id === id)?.name ?? ''

  const addUnit = () => {
    if (units.length < maxUnitOpinions) {
      setUnits([...units, { uid: nextUid, unitName: '', participationStatusId: null, reason: '' }])
      setNextUid((n) => n + 1)
    }
  }
  const setUnit = (i: number, patch: Partial<UnitRow>) =>
    setUnits(units.map((u, idx) => (idx === i ? { ...u, ...patch } : u)))
  const removeUnit = (i: number) => setUnits(units.filter((_, idx) => idx !== i))

  const onValid = (v: ActivityFormValues) => {
    const scored = Object.entries(scores).filter(([, s]) => s != null)
    if (scored.length > 0 && scored.length !== lk.criteria.length) {
      setSubError(t('activity.form.scoring.allRequired'))
      return
    }
    if (stakeholderIds.includes(OTHER_STAKEHOLDER_ID) && !stakeholderOtherTr.trim()) {
      setSubError(t('validation.required'))
      return
    }
    const validUnits = units.filter(
      (u) => u.unitName.trim() && u.participationStatusId != null && u.reason.trim(),
    )
    setSubError(null)
    setPending({
      nameTr: v.nameTr,
      nameEn: v.nameEn || null,
      organizingInstitution: v.organizingInstitution,
      startDate: v.startDate,
      endDate: v.endDate,
      numberOfParticipants: v.numberOfParticipants as number,
      countryId: v.countryId as number,
      cityId: v.cityId as number,
      eventTypeId: v.eventTypeId as number,
      otherEventType: v.eventTypeId === OTHER_EVENT_TYPE_ID ? v.otherEventType || null : null,
      invitationTypeId: v.invitationTypeId as number,
      featuredParticipants: v.featuredParticipants || null,
      website: v.website || null,
      scores: scored.map(([cid, s]) => ({ criteriaScoreId: Number(cid), score: s as number })),
      stakeholders: stakeholderIds.map((id) => ({
        stakeholderId: id,
        otherDescriptionTr: id === OTHER_STAKEHOLDER_ID ? stakeholderOtherTr.trim() : null,
      })),
      unitOpinions: validUnits.map((u) => ({
        unitName: u.unitName.trim(),
        participationStatusId: u.participationStatusId as number,
        reason: u.reason.trim(),
      })),
    })
  }

  const onSend = () => {
    if (!pending) return
    createMut.mutate(
      { dto: pending, files },
      {
        onSuccess: () => {
          toast.success(t('activity.form.toast.created'))
          navigate('/events/list')
        },
        onError: () => toast.error(t('activity.form.errors.generic')),
      },
    )
  }

  // Canlı skor önizleme (backend ile aynı formül: puan×ağırlık/5). Yetkili skor create'te backend'ce hesaplanır.
  const rowScore = (c: { id: number; weight: number }): number | null => {
    const v = scores[c.id]
    return v != null ? Math.round((v * c.weight) / 5) : null
  }
  const anyScored = lk.criteria.some((c) => scores[c.id] != null)
  const totalScore = anyScored
    ? Math.round(
        lk.criteria.reduce((sum, c) => {
          const v = scores[c.id]
          return v != null ? sum + (v * c.weight) / 5 : sum
        }, 0),
      )
    : 0
  const bands = [...lk.scoreRanges].sort((a, b) => a.startScore - b.startScore)
  let activeBand = 0
  bands.forEach((r, i) => {
    if (totalScore >= r.startScore) activeBand = i
  })
  const bandLevel = (i: number) => (i <= 0 ? 'low' : i >= bands.length - 1 ? 'high' : 'mid')

  return (
    <div className="ui-page">
      <PageHeader
        title={t('activity.form.title')}
        actions={
          <Button
            label={t('activity.form.create')}
            icon="pi pi-arrow-right"
            onClick={handleSubmit(onValid)}
          />
        }
      />

      <section className="ev-sec">
        <SectionHead no={1} name={t('activity.form.sections.basics')} />
        <div className="ev-card">
          <div className="ev-form-col">
            <FormTextField
              control={control}
              name="nameTr"
              label={t('activity.form.fields.nameTr')}
              required
              autoFocus
            />
            <div className="ev-pair">
              <FormTextField
                control={control}
                name="organizingInstitution"
                label={t('activity.form.fields.organizingInstitution')}
                required
              />
              <FormNumberField
                control={control}
                name="numberOfParticipants"
                label={t('activity.form.fields.participants')}
                required
                min={0}
              />
            </div>
            <div className="ev-pair">
              <FormDateField
                control={control}
                name="startDate"
                label={t('activity.form.fields.startDate')}
                required
              />
              <FormDateField
                control={control}
                name="endDate"
                label={t('activity.form.fields.endDate')}
                required
              />
            </div>
            <div className="ev-pair">
              <FormDropdownField
                control={control}
                name="countryId"
                label={t('activity.form.fields.country')}
                required
                options={countryOptions()}
              />
              <FormDropdownField
                control={control}
                name="cityId"
                label={t('activity.form.fields.city')}
                required
                options={optsSorted(citiesQuery.data ?? [])}
              />
            </div>
            <div className="ev-pair">
              <FormDropdownField
                control={control}
                name="eventTypeId"
                label={t('activity.form.fields.eventType')}
                required
                options={opts(lk.eventTypes)}
              />
              <FormDropdownField
                control={control}
                name="invitationTypeId"
                label={t('activity.form.fields.invitationType')}
                required
                options={opts(lk.invitationTypes)}
              />
            </div>
            {eventTypeId === OTHER_EVENT_TYPE_ID && (
              <FormTextField
                control={control}
                name="otherEventType"
                label={t('activity.form.fields.otherEventType')}
                required
              />
            )}
            <FormTextField
              control={control}
              name="featuredParticipants"
              label={t('activity.form.fields.featured')}
            />
            <FormTextField
              control={control}
              name="website"
              label={t('activity.form.fields.website')}
            />
          </div>
        </div>
      </section>

      <section className="ev-sec">
        <SectionHead no={2} name={t('activity.form.sections.scoring')} />
        <div className="ev-score-row">
          <div className="ev-card">
            <div className="ev-score-head">
              <h3 className="ev-score-title">{t('activity.form.scoring.panelTitle')}</h3>
            </div>
            <table className="ev-score-table">
              <thead>
                <tr>
                  <th>{t('activity.form.scoring.criterion')}</th>
                  <th className="num">{t('activity.form.scoring.weight')}</th>
                  <th className="puan">{t('activity.form.scoring.score')}</th>
                  <th className="num">{t('activity.form.scoring.scoreCol')}</th>
                </tr>
              </thead>
              <tbody>
                {lk.criteria.map((c) => {
                  const rs = rowScore(c)
                  return (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td className="num">{c.weight}</td>
                      <td className="puan">
                        <Dropdown
                          value={scores[c.id] ?? null}
                          options={SCORE_OPTIONS}
                          showClear
                          placeholder=""
                          scrollHeight="20rem"
                          onChange={(e) => setScores({ ...scores, [c.id]: e.value ?? undefined })}
                        />
                      </td>
                      <td className="num">{rs ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="ev-card">
            <div className="ev-score-head">
              <h3 className="ev-score-title">{t('activity.form.scoring.total')}</h3>
            </div>
            <div className="ev-total">
              <ScoreGauge score={totalScore} tone={bandLevel(activeBand)} />
              {bands.length > 0 && (
                <>
                  <div className="ev-bands">
                    {bands.map((r, i) => (
                      <div
                        key={r.id}
                        className={`ev-band${i === activeBand ? ' on ' + bandLevel(i) : ''}`}
                      >
                        <span className="r">
                          {r.startScore}–{r.endScore}
                        </span>
                        <span className="n">{r.name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="ev-gm-note">
                    {bands[bands.length - 1].startScore}–100:{' '}
                    {t('activity.form.scoring.gmRecommend')}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="ev-sec">
        <SectionHead no={3} name={t('activity.form.sections.stakeholders')} />
        <div className="ev-card">
          <MultiSelect
            value={stakeholderIds}
            options={opts(lk.stakeholders)}
            onChange={(e) => setStakeholderIds(e.value as number[])}
            placeholder={t('activity.form.stakeholders.select')}
            filter={false}
            scrollHeight="250px"
            selectAllLabel={t('actions.filter.all')}
            panelClassName="ev-stakeholder-panel"
            panelHeaderTemplate={(e) => (
              <div className={e.className}>
                <div className="ev-ms-selectall">
                  {e.checkboxElement}
                  <span>{t('activity.form.stakeholders.selectAll')}</span>
                </div>
                {e.closeElement}
              </div>
            )}
          />
          {stakeholderIds.includes(OTHER_STAKEHOLDER_ID) && (
            <div style={{ marginTop: 10 }}>
              <FormField
                label={t('activity.form.stakeholders.otherDesc')}
                htmlFor="stakeholderOther"
                required
              >
                <TextInput
                  id="stakeholderOther"
                  value={stakeholderOtherTr}
                  onChange={(e) => setStakeholderOtherTr(e.target.value)}
                />
              </FormField>
            </div>
          )}
        </div>
      </section>

      <section className="ev-sec">
        <SectionHead no={4} name={t('activity.form.sections.unitOpinions')} />
        <div className="ev-card">
          <div className="ev-score-head">
            <h3 className="ev-score-title">{t('activity.form.unitOpinions.panelTitle')}</h3>
            <span className="ev-score-note">
              {t('activity.form.unitOpinions.maxShort', { max: maxUnitOpinions })}
            </span>
          </div>
          <table className="ev-score-table ev-unit-table">
            <thead>
              <tr>
                <th>{t('activity.form.unitOpinions.unit')}</th>
                <th>{t('activity.form.unitOpinions.preference')}</th>
                <th>{t('activity.form.unitOpinions.reason')}</th>
                {/* Sil sütunu başlıksızdır; `aria-hidden` BİLEREK yok — bir `<th>`'yi
                    erişilebilirlik ağacından çıkarmak satır hücrelerinin sütunla
                    ilişkisini bozar (Sonar `typescript:S6825`). Hücredeki düğmenin
                    kendi `aria-label`'ı zaten var. */}
                <th />
              </tr>
            </thead>
            <tbody>
              {units.length === 0 && (
                <tr>
                  <td className="ev-unit-empty" colSpan={4}>
                    {t('activity.form.unitOpinions.empty')}
                  </td>
                </tr>
              )}
              {units.map((u, i) => (
                <tr key={u.uid}>
                  <td>
                    <TextInput
                      value={u.unitName}
                      onChange={(e) => setUnit(i, { unitName: e.target.value })}
                    />
                  </td>
                  <td>
                    <Dropdown
                      value={u.participationStatusId ?? null}
                      options={opts(lk.participationStatuses)}
                      onChange={(e) => setUnit(i, { participationStatusId: e.value })}
                      placeholder=""
                    />
                  </td>
                  <td>
                    <TextInput
                      value={u.reason}
                      onChange={(e) => setUnit(i, { reason: e.target.value })}
                    />
                  </td>
                  <td className="ev-unit-del">
                    <Button
                      type="button"
                      text
                      rounded
                      icon="pi pi-times"
                      aria-label={t('activity.stakeholders.actions.delete')}
                      onClick={() => removeUnit(i)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ev-unit-actions">
            <Button
              type="button"
              outlined
              icon="pi pi-plus"
              label={t('activity.form.unitOpinions.add')}
              onClick={addUnit}
              disabled={units.length >= maxUnitOpinions}
            />
          </div>
        </div>
      </section>

      <section className="ev-sec">
        <SectionHead no={5} name={t('activity.form.sections.files')} />
        <div className="ev-card">
          <FileUpload
            files={files}
            onChange={setFiles}
            accept={ATTACHMENT_ACCEPT}
            label={t('activity.form.files.dropzone')}
          />
        </div>
      </section>

      {subError && (
        <div className="ui-field-err" role="alert">
          {subError}
        </div>
      )}

      <Dialog
        header={t('activity.form.review.title')}
        visible={pending != null}
        onHide={() => setPending(null)}
        style={{ width: '94vw', maxWidth: 680 }}
        footer={
          <div className="ev-review-actions">
            <Button
              type="button"
              text
              icon="pi pi-angle-left"
              label={t('activity.form.review.back')}
              onClick={() => setPending(null)}
            />
            <Button
              type="button"
              label={t('activity.form.review.send')}
              icon="pi pi-arrow-right"
              iconPos="right"
              loading={createMut.isPending}
              onClick={onSend}
            />
          </div>
        }
      >
        {pending &&
          (() => {
            const row = (k: string, v: string) => (
              <div className="ev-review-row" key={k}>
                <span className="ev-review-k">{k}</span>
                <span className="ev-review-v">{v}</span>
              </div>
            )
            const eventTypeName =
              pending.otherEventType || nameById(lk.eventTypes, pending.eventTypeId)
            const stakeholderNames = (pending.stakeholders ?? []).map((s) =>
              s.stakeholderId === OTHER_STAKEHOLDER_ID
                ? s.otherDescriptionTr || t('activity.form.stakeholders.other')
                : nameById(lk.stakeholders, s.stakeholderId),
            )
            const level = bandLevel(activeBand)
            return (
              <div className="ev-review">
                <p className="ev-review-hint">{t('activity.form.review.desc')}</p>

                <div className="ev-review-sec">
                  <div className="ev-review-sec-title">{t('activity.form.sections.basics')}</div>
                  <div className="ev-review-panel ev-review-meta">
                    {row(t('activity.form.fields.nameTr'), pending.nameTr)}
                    {row(
                      t('activity.form.fields.organizingInstitution'),
                      pending.organizingInstitution,
                    )}
                    {row(
                      t('activity.form.review.dateRange'),
                      `${formatDate(pending.startDate)} – ${formatDate(pending.endDate)}`,
                    )}
                    {row(
                      t('activity.form.review.countryCity'),
                      `${nameById(lk.countries, pending.countryId)} / ${nameById(citiesQuery.data ?? [], pending.cityId)}`,
                    )}
                    {row(t('activity.form.fields.eventType'), eventTypeName)}
                    {row(
                      t('activity.form.fields.invitationType'),
                      nameById(lk.invitationTypes, pending.invitationTypeId),
                    )}
                    {row(
                      t('activity.form.fields.participants'),
                      pending.numberOfParticipants.toLocaleString(intlLocale(i18n.language)),
                    )}
                    {row(t('activity.form.fields.featured'), pending.featuredParticipants || '—')}
                  </div>
                </div>

                <div className="ev-review-sec">
                  <div className="ev-review-sec-title">{t('activity.form.review.secScore')}</div>
                  <div className="ev-review-panel">
                    {anyScored ? (
                      <div className="ev-review-score">
                        <div className="ev-review-score-num" style={{ color: GAUGE_COLOR[level] }}>
                          {totalScore}
                          <span>/100</span>
                        </div>
                        {bands[activeBand] && (
                          <Badge tone={BAND_TONE[level]} variant="soft">
                            {bands[activeBand].name}
                          </Badge>
                        )}
                        {level === 'high' && (
                          <span className="ev-review-gm">
                            {t('activity.form.scoring.gmRecommend')}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="ev-review-empty">{t('activity.form.review.scoreNone')}</div>
                    )}
                  </div>
                </div>

                <div className="ev-review-sec">
                  <div className="ev-review-sec-title">
                    {t('activity.form.review.secStakeUnits')}
                  </div>
                  <div className="ev-review-panel">
                    <div className="ev-review-stake">
                      <b>{t('activity.form.review.stakeholdersLabel')}:</b>{' '}
                      {stakeholderNames.length
                        ? stakeholderNames.join(', ')
                        : t('activity.form.review.noStakeholders')}
                    </div>
                    {(pending.unitOpinions?.length ?? 0) > 0 ? (
                      <table className="ev-score-table ev-review-table">
                        <thead>
                          <tr>
                            <th>{t('activity.form.unitOpinions.unit')}</th>
                            <th>{t('activity.form.unitOpinions.preference')}</th>
                            <th>{t('activity.form.unitOpinions.reason')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pending.unitOpinions!.map((u, i) => (
                            <tr key={i}>
                              <td>{u.unitName}</td>
                              <td>{nameById(lk.participationStatuses, u.participationStatusId)}</td>
                              <td>{u.reason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="ev-review-empty">{t('activity.form.review.noUnits')}</div>
                    )}
                  </div>
                </div>

                <div className="ev-review-files">
                  <i className="pi pi-paperclip" aria-hidden />{' '}
                  {files.length
                    ? files.map((f) => f.name).join(', ')
                    : t('activity.form.review.noFiles')}
                </div>
              </div>
            )
          })()}
      </Dialog>
    </div>
  )
}
